import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, formatApiError } from "../../lib/api";

const EMPTY = { quote: "", name: "", car: "" };
const inputCls = "w-full bg-[#0D1117] border border-[#263342] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-crimson transition-colors";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2";

export default function TestimonialsManager() {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/testimonials");
      setItems(data);
    } catch (err) {
      toast.error("Could not load testimonials", { description: formatApiError(err) });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ quote: t.quote, name: t.name, car: t.car || "" }); setDialogOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/testimonials/${editing.id}`, form);
        toast.success("Testimonial updated");
      } else {
        await api.post("/testimonials", form);
        toast.success("Testimonial added");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error("Save failed", { description: formatApiError(err) });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/testimonials/${id}`);
      toast.success("Testimonial removed");
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error("Delete failed", { description: formatApiError(err) });
    }
  };

  return (
    <div data-testid="testimonials-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display font-bold uppercase tracking-tight text-2xl text-slate-100">Customer Reviews</h2>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mt-1">Shown in the testimonials section</p>
        </div>
        <button
          data-testid="testimonial-add-button"
          onClick={openAdd}
          className="flex items-center gap-2 bg-crimson text-white font-display font-semibold uppercase tracking-widest text-sm px-5 py-2.5 hover:-translate-y-0.5 transition-transform"
        >
          <Plus size={16} /> Add Review
        </button>
      </div>

      <div className="space-y-3" data-testid="testimonials-manager-list">
        {items.map((t, i) => (
          <div key={t.id} className="bg-[#0D1117] border border-[#263342] p-5" data-testid={`testimonial-admin-row-${i}`}>
            <p className="text-sm text-slate-300 leading-relaxed line-clamp-2">"{t.quote}"</p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <span className="font-display font-bold uppercase text-slate-100">{t.name}</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{t.car}</span>
              <span className="flex-1" />
              <button
                data-testid={`testimonial-edit-${i}`}
                onClick={() => openEdit(t)}
                className="flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] border border-[#263342] px-3 py-2 text-slate-300 hover:border-crimson hover:text-crimson transition-colors"
              >
                <Pencil size={12} /> Edit
              </button>
              {confirmDelete === t.id ? (
                <button
                  data-testid={`testimonial-delete-confirm-${i}`}
                  onClick={() => remove(t.id)}
                  className="flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] border border-red-500 bg-red-500/10 px-3 py-2 text-red-400"
                >
                  <Trash2 size={12} /> Confirm?
                </button>
              ) : (
                <button
                  data-testid={`testimonial-delete-${i}`}
                  onClick={() => setConfirmDelete(t.id)}
                  className="flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] border border-[#263342] px-3 py-2 text-slate-400 hover:border-red-500 hover:text-red-400 transition-colors"
                >
                  <Trash2 size={12} /> Delete
                </button>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <p className="font-mono text-sm text-slate-500 text-center py-8" data-testid="testimonials-empty">No reviews yet. Add your first one.</p>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-panel border-[#263342] text-slate-100 max-w-lg" data-testid="testimonial-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-extrabold uppercase tracking-tight text-2xl">
              {editing ? "Edit Review" : "Add Review"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="mt-2 space-y-4" data-testid="testimonial-form">
            <div>
              <label className={labelCls}>Quote *</label>
              <textarea required rows={4} className={inputCls} data-testid="testimonial-form-quote" value={form.quote} onChange={(e) => setForm((f) => ({ ...f, quote: e.target.value }))} placeholder="What the customer said..." />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Customer name *</label>
                <input required className={inputCls} data-testid="testimonial-form-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <label className={labelCls}>Their car</label>
                <input className={inputCls} data-testid="testimonial-form-car" value={form.car} onChange={(e) => setForm((f) => ({ ...f, car: e.target.value }))} placeholder="BMW M4" />
              </div>
            </div>
            <button type="submit" data-testid="testimonial-form-save" disabled={saving} className="w-full bg-crimson text-white font-display font-semibold uppercase tracking-widest py-3.5 disabled:opacity-60">
              {saving ? "Saving..." : editing ? "Save Changes" : "Add Review"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
